/*
One password rule, everywhere (XOL-602/603/604, D8).

A password is valid when, after trimming, it is 8 to 72 characters, does not contain the
Login ID (case-insensitive), and equals its confirmation. It applies to the set/reset page,
the invite-to-register page and Change Password. NOT the public self-registration page
(Register.vue): Carlos, 2026-10-19, that page stays as it was; Tower Co invitations have their own page. The API enforces the same rule
(App\Rules\PasswordPolicy) and uses the same three strings for its 422s, so the client and
the server never disagree in wording.

Existing passwords are not re-checked; this runs on new input only.

`passwordProblem` returns the FIRST failure, in this order:
  too_short      under PASSWORD_MIN. No alert: the permanent hint under the field covers it,
                 and the Save button stays disabled.
  too_long       over PASSWORD_MAX
  contains_login contains the Login ID
  mismatch       the confirmation differs
or null when the pair is acceptable.

Length is counted in characters (code points), as the API counts it, not in UTF-16 units.

ES5, no optional chaining or nullish coalescing (Babel 6).
------------------------------------------------------------------------ */

export var PASSWORD_MIN = 8
export var PASSWORD_MAX = 72

// The permanent hint under New Password (PasswordForm).
export var PASSWORD_HINT = 'Use ' + PASSWORD_MIN + ' to ' + PASSWORD_MAX + ' characters. Do not include the Login ID.'

// What the alert says for each problem. too_short has none, by design.
export var PASSWORD_PROBLEM_TEXT = {
  too_long: 'Use ' + PASSWORD_MAX + ' characters or fewer.',
  contains_login: 'The password must not contain the Login ID.',
  mismatch: "The passwords don't match."
}

// The one place a too-short password is NAMED in words: a form whose Save is not disabled by
// the problem (the create-time password on ContactDetail) has to say why it blocked the save.
// Same string as the API's. The password forms with a disabled Save keep the hint alone.
export var PASSWORD_TOO_SHORT_TEXT = 'Use at least ' + PASSWORD_MIN + ' characters.'

function clean (value) {
  return typeof value === 'string' ? value.trim() : ''
}

// Characters, not UTF-16 code units: a surrogate pair counts once.
function charLength (value) {
  return value.replace(/[\uD800-\uDBFF][\uDC00-\uDFFF]/g, '_').length
}

/**
 * @param {*} password the new password (trimmed here)
 * @param {*} confirm the confirmation (trimmed here)
 * @param {*} login the Login ID the password must not contain (optional)
 * @returns {string|null} 'too_short' | 'too_long' | 'contains_login' | 'mismatch' | null
 */
export function passwordProblem (password, confirm, login) {
  var pw = clean(password)
  var length = charLength(pw)

  if (length < PASSWORD_MIN) {
    return 'too_short'
  }
  if (length > PASSWORD_MAX) {
    return 'too_long'
  }

  var id = clean(login).toLowerCase()
  if (id && pw.toLowerCase().indexOf(id) !== -1) {
    return 'contains_login'
  }

  if (pw !== clean(confirm)) {
    return 'mismatch'
  }

  return null
}

// The alert text for a problem key; '' for null and for too_short.
export function passwordProblemText (problem) {
  return (problem && PASSWORD_PROBLEM_TEXT[problem]) || ''
}

/**
 * The problem to SHOW in the alert, or null for none. The one gate every password form
 * shares (Change Password, the set/reset page, the invite page):
 *  - nothing until the confirm field has been left once (`touched`), then live, so a correct
 *    entry is never called wrong mid-keystroke;
 *  - never `too_short`: the permanent hint under the field covers it, and Save stays disabled;
 *  - a mismatch needs content in BOTH fields, so an empty confirm is not an accusation.
 *
 * @returns {string|null} 'too_long' | 'contains_login' | 'mismatch' | null
 */
export function visiblePasswordProblem (password, confirm, login, touched) {
  if (!touched) {
    return null
  }
  var problem = passwordProblem(password, confirm, login)
  if (!problem || problem === 'too_short') {
    return null
  }
  if (problem === 'mismatch' && !(password && confirm)) {
    return null
  }
  return problem
}
